import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { AccountBalance as CurrencyIcon } from '@mui/icons-material';
import { getProfile, createProfile, updateProfile } from '../../services/profileService';
import PendingInvitations from '../Groups/PendingInvitations';
import { useGroupContext } from '../Groups/useGroupContext';
import { CURRENCIES, formatCurrency } from '../utils/currencyFormatter';
import { AuthContext } from '../Auth/AuthContext';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    display_name: '',
    preferred_currency: 'USD'
  });
  const [invitationUpdate, setInvitationUpdate] = useState(null);
  const { loadUserGroups } = useGroupContext(); 
  const { refreshUserCurrency } = useContext(AuthContext);

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleInvitationUpdate = (action, invitation, result) => {
    if (action === 'accepted') {
      setInvitationUpdate(`Successfully joined ${invitation.group_name}!`);
      loadUserGroups();
      
      setTimeout(() => setInvitationUpdate(null), 5000);
    } else if (action === 'declined') {
      setInvitationUpdate(`Declined invitation to ${invitation.group_name}`);
      setTimeout(() => setInvitationUpdate(null), 3000);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const profileData = await getProfile();
      
      if (profileData && profileData.length > 0) {
        const userProfile = profileData[0];
        setProfile(userProfile);
        setFormData({
          email: userProfile.email || '',
          display_name: userProfile.display_name || '',
          preferred_currency: userProfile.preferred_currency || 'USD'
        });
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCurrencyChange = (e) => {
    setFormData(prev => ({
      ...prev,
      preferred_currency: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      if (profile) {
        await updateProfile(profile.id, formData);
      } else {
        await createProfile(formData);
      }
      
      setSuccess(true);
      await fetchProfile();
      
      await refreshUserCurrency();
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setError(profile ? 'Failed to update profile' : 'Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profile Management
        </Typography>
        
        <Paper sx={{ p: 4, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {profile ? 'Update Your Profile' : 'Create Your Profile'}
          </Typography>
          
          <Typography variant="body2" color="textSecondary" paragraph>
            {profile 
              ? 'Update your display name, email address, and preferred currency.'
              : 'Please complete your profile to start using groups and sharing features.'
            }
          </Typography>

          <Divider sx={{ my: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Profile {profile ? 'updated' : 'created'} successfully!
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email Address"
              name="email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              helperText="Your email address for group invitations and notifications"
              slotProps={{
                input: {
                  readOnly: true,
                  disabled: true
                }
              }}
            />

            <TextField
              label="Display Name"
              name="display_name"
              fullWidth
              value={formData.display_name}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              helperText="This name will be visible to other group members"
            />

            {/* Currency Selector */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="currency-select-label">Preferred Currency</InputLabel>
              <Select
                labelId="currency-select-label"
                value={formData.preferred_currency}
                onChange={handleCurrencyChange}
                label="Preferred Currency"
                startAdornment={<CurrencyIcon sx={{ mr: 1, color: 'action.active' }} />}
              >
                {Object.entries(CURRENCIES).map(([code, currency]) => (
                  <MenuItem key={code} value={code}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={currency.symbol} 
                        size="small" 
                        sx={{ 
                          minWidth: '40px',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold'
                        }} 
                      />
                      <Typography variant="body2">
                        {currency.name} ({code})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, ml: 1 }}>
                This currency will be used for displaying amounts throughout the application
                {formData.preferred_currency && (
                  <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                    Example: {formatCurrency(1234.56, formData.preferred_currency)}
                  </span>
                )}
              </Typography>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              fullWidth
            >
              {saving 
                ? (profile ? 'Updating...' : 'Creating...') 
                : (profile ? 'Update Profile' : 'Create Profile')
              }
            </Button>
          </form>

          {profile && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Profile Information
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {new Date(profile.created_at).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Last Updated:</strong> {new Date(profile.updated_at).toLocaleDateString()}
              </Typography>
              {profile.preferred_currency && (
                <Typography variant="body2">
                  <strong>Currency:</strong> {CURRENCIES[profile.preferred_currency]?.name} ({profile.preferred_currency})
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 4, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Group Invitations
          </Typography>
          
          <Typography variant="body2" color="textSecondary" paragraph>
            Manage your pending group invitations below.
          </Typography>

          {invitationUpdate && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {invitationUpdate}
            </Alert>
          )}

          <PendingInvitations onInvitationUpdate={handleInvitationUpdate} />
        </Paper>
      </Box>
    </Container>
  );
};

export default ProfilePage;